import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { FloppyDisk, Key, UserCircle } from "@phosphor-icons/react";

const LABEL = "mb-1.5 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";
const INPUT = "w-full border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200";

export default function ProfilePage() {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [savingP, setSavingP] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => { if (user) setProfile({ name: user.name || "", email: user.email || "" }); }, [user]);

  const saveProfile = async () => {
    if (!profile.email.trim()) { toast.error(t.email + " *"); return; }
    setSavingP(true);
    try {
      await axios.put(`${API}/me/profile`, { name: profile.name, email: profile.email });
      await refreshUser();
      toast.success(t.profileUpdated);
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setSavingP(false); }
  };

  const changePassword = async () => {
    if (pwd.new_password.length < 6) { toast.error(t.passwordHint); return; }
    setSavingPwd(true);
    try {
      await axios.post(`${API}/me/password`, pwd);
      toast.success(t.passwordUpdated);
      setPwd({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setSavingPwd(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-2xl px-4 sm:px-6 py-8">
        <div className="mb-6 flex items-center gap-3 border-b border-border pb-5">
          <UserCircle size={32} weight="duotone" className="text-primary" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{user?.role === "admin" ? t.roleAdmin : t.roleSalesRep}</div>
            <h1 className="font-display text-2xl font-black tracking-tighter text-foreground">{t.myProfile}</h1>
          </div>
        </div>

        <div className="mb-6 border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-bold tracking-tight text-foreground">{t.profileInfo}</h2>
          <div className="space-y-4">
            <label className="block"><span className={LABEL}>{t.name}</span>
              <input data-testid="profile-name" className={INPUT} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></label>
            <label className="block"><span className={LABEL}>{t.email}</span>
              <input data-testid="profile-email" className={INPUT} value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} /></label>
          </div>
          <div className="mt-5">
            <button data-testid="profile-save" onClick={saveProfile} disabled={savingP}
              className="inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              <FloppyDisk size={16} weight="bold" /> {t.save}
            </button>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold tracking-tight text-foreground"><Key size={18} weight="bold" className="text-primary" /> {t.updatePassword}</h2>
          <div className="space-y-4">
            <label className="block"><span className={LABEL}>{t.currentPassword}</span>
              <input data-testid="profile-current-password" type="password" className={INPUT} value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} /></label>
            <label className="block"><span className={LABEL}>{t.newPassword}</span>
              <input data-testid="profile-new-password" type="password" className={INPUT} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} /></label>
          </div>
          <div className="mt-5">
            <button data-testid="profile-change-password" onClick={changePassword} disabled={savingPwd}
              className="inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              <Key size={16} weight="bold" /> {t.updatePassword}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
